// Small shared primitives. Anything feature-specific lives in features/.
import { useEffect, useRef, type ReactNode } from 'react'
import { initials } from '../lib/format'

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 p-8 text-stone" role="status">
      <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-stone/40 border-t-interlochen" />
      <span>{label}</span>
    </div>
  )
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="card flex flex-col items-center gap-2 py-10 text-center">
      <p className="font-semibold text-ink">{title}</p>
      {hint && <p className="max-w-sm text-sm text-stone">{hint}</p>}
      {action}
    </div>
  )
}

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-danger/40 bg-danger/10 px-4 py-3 text-danger" role="alert">
      {message}
    </div>
  )
}

export function OfflineBanner({ asOf }: { asOf: string | null }) {
  return (
    <div className="bg-sunset/20 px-4 py-2 text-center text-sm text-clay">
      Offline — showing the schedule as of {asOf ?? 'your last visit'}.
    </div>
  )
}

export function CountBadge({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <span
      className="tnum inline-flex min-w-[20px] items-center justify-center rounded-full bg-danger px-1.5 text-xs font-bold text-white"
      aria-label={`${count} waiting`}
    >
      {count > 99 ? '99+' : count}
    </span>
  )
}

const CHIP_STYLES: Record<string, string> = {
  // request states
  pending_peer: 'bg-sunset/20 text-clay',
  pending_approval: 'bg-sunset/20 text-clay',
  pending_overtime: 'bg-danger/10 text-danger',
  approved: 'bg-forest/10 text-forest',
  denied: 'bg-danger/10 text-danger',
  cancelled: 'bg-stone/10 text-stone',
  superseded: 'bg-stone/10 text-stone',
  expired: 'bg-stone/10 text-stone',
  // shift states
  open: 'bg-sunbeam/30 text-clay',
  assigned: 'bg-forest/10 text-forest',
  draft: 'bg-sand/60 text-stone',
  published: 'bg-interlochen/10 text-interlochen',
  ot: 'bg-danger/10 text-danger',
}

const CHIP_LABELS: Record<string, string> = {
  pending_peer: 'Awaiting partner',
  pending_approval: 'Pending approval',
  pending_overtime: 'Needs director (OT)',
  approved: 'Approved',
  denied: 'Denied',
  cancelled: 'Cancelled',
  superseded: 'Superseded',
  expired: 'Expired',
  open: 'OPEN',
  assigned: 'Assigned',
  draft: 'Draft',
  published: 'Published',
  ot: 'OT',
}

export function StatusChip({ status }: { status: string }) {
  return (
    <span className={`chip ${CHIP_STYLES[status] ?? 'bg-stone/10 text-stone'}`}>
      {CHIP_LABELS[status] ?? status}
    </span>
  )
}

export function Avatar({ name, photoUrl, size = 32 }: { name: string; photoUrl?: string | null; size?: number }) {
  const style = { width: size, height: size, fontSize: size * 0.4 }
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        style={style}
        className="rounded-full object-cover"
        onError={(e) => {
          ;(e.target as HTMLImageElement).style.display = 'none'
        }}
      />
    )
  }
  return (
    <span
      style={style}
      className="inline-flex items-center justify-center rounded-full bg-lake/30 font-semibold text-interlochen"
      aria-hidden
    >
      {initials(name)}
    </span>
  )
}

/**
 * Adaptive modal: centered dialog ≥768px, bottom sheet below. Focus is
 * trapped while open and returned to the invoker on close.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const restoreRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return
    restoreRef.current = document.activeElement as HTMLElement
    const el = ref.current
    el?.querySelector<HTMLElement>('button, [href], input, select, textarea')?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'Tab' && el) {
        const focusables = el.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]')
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      restoreRef.current?.focus()
    }
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/50 md:items-center" onClick={onClose}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90dvh] w-full flex-col rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)] md:max-w-lg md:rounded-lg"
      >
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-stone/30 md:hidden" aria-hidden />
        <div className="flex items-center justify-between border-b border-hairline px-5 py-3">
          <h2 className="text-lg font-bold text-interlochen">{title}</h2>
          <button className="btn-ghost !min-h-0 px-2 py-1" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="sticky bottom-0 border-t border-hairline bg-white px-5 py-3">{footer}</div>}
      </div>
    </div>
  )
}
